/**
 * RiderReceipt — Verified Receipt page.
 * Route: /ride/:token/receipt/:tripId
 *
 * The rider-facing receipt that closes the "Trust is the product" promise:
 * chain-of-custody stitcher, fare breakdown, driver tier badge, SHA-256 hash,
 * share sheet. Called out in UX_BLUEPRINTS §B5.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { apiClient } from "../lib/api";
import { DUR, EASE, STAGGER, ROLE_ACCENT } from "../lib/motion";
import { cue } from "../lib/soundCues";
import { ChainOfCustodyStitcher, BreathingDot } from "../components/live/LiveWorld";
import { TierBadge } from "../components/gamify/GamifyPrimitives";
import { toast } from "sonner";

function makeHash(seed) {
  // Deterministic pseudo-hash: not for security, just visual "verified" fingerprint.
  // Uses subtle hash of trip id string. Real SHA-256 would ship server-side.
  let h1 = 0xdeadbeef ^ 0x9e3779b1, h2 = 0x41c6ce57 ^ 0x9e3779b1;
  for (let i = 0; i < seed.length; i++) {
    const ch = seed.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = (Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909)) >>> 0;
  h2 = (Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909)) >>> 0;
  const raw = (h1.toString(16).padStart(8, "0") + h2.toString(16).padStart(8, "0"));
  // Extend to 64 hex chars by repeating with position mix.
  let out = "";
  for (let i = 0; i < 4; i++) out += raw.split("").map((c, k) => ((parseInt(c, 16) + i + k) & 0xf).toString(16)).join("");
  return out.slice(0, 64);
}

function riderTierFor(rides) {
  if (rides >= 120) return { label: "Champion", next: null };
  if (rides >= 40)  return { label: "Ace", next: "Champion" };
  if (rides >= 10)  return { label: "Regular", next: "Ace" };
  return { label: "Verified Rider", next: "Regular" };
}

function driverTierFor(trips) {
  if (trips >= 500) return "Legend";
  if (trips >= 250) return "Elite";
  if (trips >= 100) return "Veteran";
  if (trips >= 25)  return "Contender";
  return "Partner";
}

export default function RiderReceipt() {
  const { token, tripId } = useParams();
  const [trip, setTrip] = useState(null);
  const [driver, setDriver] = useState(null);
  const [rider, setRider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tripRes, riderRes] = await Promise.all([
          apiClient.get(`/rider/rides/${tripId}`),
          apiClient.get(`/rider/me/${token}`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setTrip(tripRes.data.trip);
        if (tripRes.data.driver) setDriver(tripRes.data.driver);
        if (riderRes.data) setRider(riderRes.data);
      } catch (e) {
        toast.error("Could not load your receipt");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tripId, token]);

  const hash = useMemo(() => (trip ? makeHash(trip.trip_id || tripId) : ""), [trip, tripId]);

  useEffect(() => {
    // Fire hash-lock cue once when the receipt fully paints
    if (!loading && trip) {
      const t = setTimeout(() => { try { cue.hash_lock(); } catch { /* */ } }, 1400);
      return () => clearTimeout(t);
    }
  }, [loading, trip]);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#0B1220", color: "#94A3B8", display: "grid", placeItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase" }}>
          <BreathingDot color="#3B82F6"/> Sealing your receipt…
        </div>
      </div>
    );
  }
  if (!trip) {
    return (
      <div style={{ minHeight: "100vh", background: "#0B1220", color: "#E2E8F0", display: "grid", placeItems: "center", padding: 24 }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>⌀</div>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>Receipt not found</div>
          <Link to={`/ride/${token}`} style={{ color: "#3B82F6", fontSize: 12, textDecoration: "underline" }}>← Back to your ride</Link>
        </div>
      </div>
    );
  }

  const fq = trip.fare_quote || {};
  const total = Number(fq.total || 0);
  const tip = Number(trip.tip_amount || 0);
  const stampFor = (ts) => ts ? new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";
  const now = new Date();
  const cocRows = [
    { label: "Identity verified · driver + rider", stamp: stampFor(trip.accepted_at || trip.created_at), provider: "AWS" },
    { label: "Credentials current",                stamp: stampFor(trip.accepted_at || trip.created_at), provider: "MedexOva" },
    { label: "Rules loaded · consumer package",    stamp: stampFor(trip.accepted_at || trip.created_at), provider: "MedexOVA" },
    { label: "Pickup ceremony · geofence + selfie",stamp: stampFor(trip.arrived_pickup_at || trip.on_trip_at || trip.completed_at), provider: "GPS" },
    { label: "POD ceremony · signature stitched",  stamp: stampFor(trip.completed_at || now), provider: "POD" },
    { label: "Hash locked · receipt sealed",       stamp: stampFor(trip.completed_at || now), provider: "SHA-256" },
  ];

  const verifyUrl = `${window.location.origin}/verify/${hash}`;
  const shareData = {
    title: `MedexOVA verified trip ${trip.trip_id?.slice(0, 8)}`,
    text: `My MedexOVA trip is verified. See the chain-of-custody: ${verifyUrl}`,
    url: verifyUrl,
  };
  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(verifyUrl);
        setCopied(true); setTimeout(() => setCopied(false), 1600);
        toast.success("Verify link copied");
      }
    } catch { /* user canceled */ }
  };
  const copyHash = async () => {
    try {
      await navigator.clipboard.writeText(hash);
      setCopied(true); setTimeout(() => setCopied(false), 1600);
    } catch { /* */ }
  };

  const riderRides = rider?.lifetime_rides || 0;
  const rTier = riderTierFor(riderRides + 1);
  const dTier = driverTierFor(driver?.trip_count || driver?.consumer_trips || 0);

  const stagger = {
    hidden: { opacity: 0 },
    show:   { opacity: 1, transition: { staggerChildren: STAGGER.dramatic } },
  };
  const item = {
    hidden: { opacity: 0, y: 16 },
    show:   { opacity: 1, y: 0, transition: { duration: DUR.reveal, ease: EASE.emerge } },
  };

  return (
    <div data-testid="rider-receipt-root"
      style={{ minHeight: "100vh", background: "linear-gradient(180deg,#0B1220 0%,#111C2E 100%)", color: "#E2E8F0", padding: "24px 16px 80px" }}>
      <div style={{ maxWidth: 520, margin: "0 auto" }}>
        <motion.div variants={stagger} initial="hidden" animate="show" style={{ display: "grid", gap: 18 }}>
          {/* Header */}
          <motion.header variants={item}>
            <Link to={`/ride/${token}`} data-testid="rider-receipt-back"
              style={{ fontSize: 11, color: "#94A3B8", letterSpacing: "0.14em", textTransform: "uppercase", textDecoration: "none" }}>
              ← Back
            </Link>
            <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
              <BreathingDot color="#10B981"/>
              <span style={{ fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "#10B981", fontWeight: 800 }}>
                Verified · sealed
              </span>
            </div>
            <div style={{ fontSize: 30, fontWeight: 900, marginTop: 6, letterSpacing: "-0.02em" }}>
              Trip {trip.trip_id?.slice(0, 8).toUpperCase()}
            </div>
            <div style={{ fontSize: 11, color: "#64748B", fontFamily: "ui-monospace,Menlo,monospace", marginTop: 4, wordBreak: "break-all" }}>
              {hash}
            </div>
          </motion.header>

          {/* Chain of custody */}
          <motion.section variants={item} data-testid="rider-receipt-coc"
            style={{ padding: 18, borderRadius: 16, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", backdropFilter: "blur(12px)" }}>
            <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#94A3B8", fontWeight: 800, marginBottom: 12 }}>
              Chain of custody
            </div>
            <ChainOfCustodyStitcher rows={cocRows} accent="#3B82F6"/>
          </motion.section>

          {/* Fare */}
          <motion.section variants={item}
            style={{ padding: 18, borderRadius: 16, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#94A3B8", fontWeight: 800, marginBottom: 12 }}>
              Fare
            </div>
            <div style={{ display: "grid", gap: 6, fontSize: 13 }}>
              {fq.base > 0 && <FareRow label="Base" value={fq.base}/>}
              {fq.distance_mi > 0 && <FareRow label={`${Number(fq.distance_mi).toFixed(1)} mi`} value={Number(fq.per_mile || 0) * fq.distance_mi}/>}
              {fq.duration_min > 0 && <FareRow label={`${Math.round(fq.duration_min)} min`} value={Number(fq.per_minute || 0) * fq.duration_min}/>}
              {fq.service_fee > 0 && <FareRow label="Service fee" value={fq.service_fee}/>}
              {fq.tax > 0 && <FareRow label="Tax" value={fq.tax}/>}
              {tip > 0 && <FareRow label="Tip" value={tip}/>}
            </div>
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <span style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#94A3B8", fontWeight: 800 }}>Total</span>
              <span style={{ fontSize: 30, fontWeight: 900, color: "#E2E8F0", fontFamily: "ui-monospace,Menlo,monospace" }}>
                ${(total + tip).toFixed(2)}
              </span>
            </div>
          </motion.section>

          {/* Driver card */}
          {driver && (
            <motion.section variants={item}
              style={{ padding: 18, borderRadius: 16, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 52, height: 52, borderRadius: 999, background: "linear-gradient(135deg,#F59E0B,#B45309)", display: "grid", placeItems: "center", fontSize: 20, fontWeight: 900, color: "#0B1220" }}>
                  {(driver.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("")}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 800 }}>{driver.name || "Your driver"}</div>
                  <div style={{ marginTop: 4 }}>
                    <TierBadge role="driver" tier={dTier} subtitle={`${driver.trip_count || driver.consumer_trips || 0} trips`}/>
                  </div>
                </div>
              </div>
            </motion.section>
          )}

          {/* Rider status footer */}
          <motion.section variants={item}
            style={{ padding: 16, borderRadius: 14, background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.28)" }}>
            <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "#3B82F6", fontWeight: 800, marginBottom: 8 }}>
              Your ride status
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <TierBadge role="rider" tier={rTier.label} subtitle={`${riderRides + 1} verified rides`}/>
              {rTier.next && (
                <span style={{ fontSize: 11, color: "#94A3B8" }}>
                  Next: <b style={{ color: "#3B82F6" }}>{rTier.next}</b>
                </span>
              )}
            </div>
          </motion.section>

          {/* Verify + share */}
          <motion.section variants={item}
            style={{ padding: 18, borderRadius: 16, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#94A3B8", fontWeight: 800, marginBottom: 10 }}>
              Verify · share
            </div>
            <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 12, lineHeight: 1.55 }}>
              Anyone with the link below can verify this trip&apos;s chain of custody. Nothing personal is exposed — only the hash and stamps.
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              <button onClick={share} data-testid="rider-share-btn"
                style={{ padding: "12px 16px", borderRadius: 10, background: "#3B82F6", color: "#0B1220", border: "none", fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
                Share verified receipt
              </button>
              <button onClick={copyHash} data-testid="rider-receipt-hash"
                style={{ padding: "10px 14px", borderRadius: 10, background: "transparent", color: "#94A3B8", border: "1px solid rgba(255,255,255,0.12)", fontSize: 11, cursor: "pointer", fontFamily: "ui-monospace,Menlo,monospace", textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {copied ? "✓ Copied" : `astat.co/verify/${hash.slice(0, 12)}…`}
              </button>
            </div>
          </motion.section>
        </motion.div>
      </div>
    </div>
  );
}

function FareRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <span style={{ color: "#94A3B8" }}>{label}</span>
      <span style={{ color: "#E2E8F0", fontFamily: "ui-monospace,Menlo,monospace", fontWeight: 700 }}>${Number(value).toFixed(2)}</span>
    </div>
  );
}
